import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    seriesData: {},
    productSelection: [],
    activeProfile: null,
    roomscene: "",
    currentView: "swatch", // 'swatch' or 'room'
    currentImageUrl: null,
    chosenPattern: null, // Selected pattern from the Rug tab pattern menu
    hiddenColors: [], // Array of color IDs that are hidden in CustomizeColors
    patternColors: [], // Array of color objects for the current pattern
    selectedColorIndex: null, // Index of the currently selected color in CustomizeColors
    selectedRoomscene: null, // Selected room scene from RoomscenesPopup
    selectedFloorProduct: null, // Selected floor product from Floor tab
    menuActiveSection: 'customize', // Active section in ProductMenu: 'pattern', 'customize', 'rug-shape-size'
    menuActiveTab: 'exterior', // Active tab in ProductMenu: 'rug' or 'floor'
    selectedFloorPlanData: null, // Selected floor plan data from StartPage
    vizLoading: false, // Loading state for the visualizer
    shareData: null, // Share link data for ShareModal
    // Zoom state
    vizZoom: 1,
    // Undo/redo snapshot state
    vizSnapshots: [],
    vizSnapIndex: 0,
    maxVizSnapshots: 20,
    muteNextSnapshot: false,
    // Flag to indicate we're restoring from a saved project (skip auto-restoration logic)
    isRestoringProject: false
};

const sharedSlice = createSlice({
    name: "shared",
    initialState,
    reducers: {
        setSeriesData(state, action) {
            state.seriesData[action.payload.seriesId] = action.payload.data;
        },

        setProductSelection(state, action) {
            state.productSelection = action.payload;
        },
        addProduct(state, action) {
            state.productSelection.push(action.payload);
        },
        removeProduct(state, action) {
            state.productSelection = state.productSelection.filter(id => id !== action.payload);
        },
        clearProductSelection(state) {
            state.productSelection = [];
        },
        setActiveProfile(state, action) {
            state.activeProfile = action.payload;
        },
        clearActiveProfile(state) {
            state.activeProfile = null;
        },
        setRoomscene(state, action) {
            state.roomscene = action.payload;
        },
        setCurrentView(state, action) {
            state.currentView = action.payload;
        },
        setCurrentImageUrl(state, action) {
            state.currentImageUrl = action.payload;
        },
        setChosenPattern(state, action) {
            state.chosenPattern = action.payload;
            state.hiddenColors = [];
            state.patternColors = [];
            state.selectedColorIndex = null;
        },
        clearChosenPattern(state) {
            state.chosenPattern = null;
            state.hiddenColors = [];
            state.patternColors = [];
            state.selectedColorIndex = null;
        },
        toggleHiddenColor(state, action) {
            const colorId = action.payload;
            const index = state.hiddenColors.indexOf(colorId);
            if (index === -1) {
                state.hiddenColors.push(colorId);
            } else {
                state.hiddenColors.splice(index, 1);
            }
        },
        clearHiddenColors(state) {
            state.hiddenColors = [];
        },
        setPatternColors(state, action) {
            state.patternColors = action.payload;
        },
        setSelectedColorIndex(state, action) {
            state.selectedColorIndex = action.payload;
        },
        updatePatternColor(state, action) {
            const { originalId, newProduct } = action.payload;
            state.patternColors = state.patternColors.map(color => {
                if (color.originalId === originalId) {
                    return {
                        ...color,
                        product: newProduct,
                        currentId: newProduct.id,
                        sku: newProduct.sku || color.sku
                    };
                }
                return color;
            });
        },
        setSelectedRoomscene(state, action) {
            state.selectedRoomscene = action.payload;
        },
        setSelectedFloorProduct(state, action) {
            state.selectedFloorProduct = action.payload;
        },
        setMenuActiveSection(state, action) {
            state.menuActiveSection = action.payload;
        },
        setMenuActiveTab(state, action) {
            state.menuActiveTab = action.payload;
        },
        setSelectedFloorPlanData(state, action) {
            state.selectedFloorPlanData = action.payload;
        },
        setVizLoading(state, action) {
            state.vizLoading = action.payload;
        },
        setShareData(state, action) {
            state.shareData = action.payload;
        },
        // Zoom actions
        setVizZoom(state, action) {
            state.vizZoom = action.payload;
        },
        zoomIn(state) {
            state.vizZoom = Math.min(state.vizZoom + 0.25, 3);
        },
        zoomOut(state) {
            state.vizZoom = Math.max(state.vizZoom - 0.25, 0.5);
        },
        zoomReset(state) {
            state.vizZoom = 1;
        },
        // Undo/redo snapshot actions
        snapshotVizState(state, action) {
            if (state.muteNextSnapshot) {
                state.muteNextSnapshot = false;
                return;
            }
            const snap = action.payload; // { specifier, sel }
            
            // If we've undone into history, discard any "future" snapshots
            if (state.vizSnapIndex < state.vizSnapshots.length - 1) {
                state.vizSnapshots = state.vizSnapshots.slice(0, state.vizSnapIndex + 1);
            }
            
            state.vizSnapshots.push(snap);
            
            // Cap history size (drop oldest)
            if (state.vizSnapshots.length > state.maxVizSnapshots) {
                state.vizSnapshots.shift();
                state.vizSnapIndex = state.vizSnapshots.length - 1;
            } else {
                state.vizSnapIndex = state.vizSnapshots.length - 1;
            }
        },
        goBackward(state) {
            if (state.vizSnapIndex <= 0) return;
            state.vizSnapIndex -= 1;
            state.muteNextSnapshot = true;
        },
        goForward(state) {
            if (state.vizSnapIndex >= state.vizSnapshots.length - 1) return;
            state.vizSnapIndex += 1;
            state.muteNextSnapshot = true;
        },
        clearVizSnapshots(state) {
            state.vizSnapshots = [];
            state.vizSnapIndex = 0;
            state.muteNextSnapshot = false;
        },
        // Project restoration flag
        setIsRestoringProject(state, action) {
            state.isRestoringProject = action.payload;
        }
    }
});

export const sharedActions = sharedSlice.actions;
export default sharedSlice.reducer;
